import React from 'react';
import { Crosshair, TrendingUp } from 'lucide-react';
import { Badge, SeverityBadge } from '@/components/primitives';
import { PriorityBadge, pct } from '@/pages/dashboard/VulnBrief';
import { isoDate } from '@/pages/dashboard/format';

const SHORT = { splunk: 'SPL', sentinel: 'KQL', elastic: 'ES', sigma: 'Sigma' };
const FULL = { splunk: 'Splunk', sentinel: 'Sentinel / Defender', elastic: 'Elastic', sigma: 'Sigma' };

function Cvss({ row }) {
  if (row.cvss_score == null) return <Badge size="sm" title="NVD has no CVSS score for this CVE yet">Unscored</Badge>;
  return (
    <span className="vp-cvss">
      <span className="dash-mono">{Number(row.cvss_score).toFixed(1)}</span>
      <SeverityBadge severity={row.cvss_severity} size="sm" />
    </span>
  );
}

function Exploitation({ row }) {
  const tags = [];
  if (row.kev) {
    tags.push(<Badge key="kev" tone="warning" size="sm" title={row.kev_ransomware_use === 'Known' ? 'On the CISA KEV list, used in ransomware' : 'On the CISA Known Exploited Vulnerabilities list'}>
      Exploited{row.kev_ransomware_use === 'Known' ? ', ransomware' : ''}
    </Badge>);
  }
  if (row.public_exploit) tags.push(<Badge key="x" size="sm" title="Public exploit code exists (Exploit-DB)">Public exploit</Badge>);
  if (!tags.length) return <span className="dash-muted">None known</span>;
  return <span className="dash-signal-row">{tags}</span>;
}

function Hunt({ row, onOpen }) {
  if (row.hunt_state === 'ready') {
    return (
      <button type="button" className="vp-hunt" onClick={(e) => { e.stopPropagation(); onOpen(row.cve_id, 'hunting'); }}
        title={`Hunting queries for ${row.hunt.map((p) => FULL[p] || p).join(', ')}. Open them.`}>
        <Crosshair aria-hidden="true" />
        {row.hunt.map((p) => <span key={p} className="vp-plat">{SHORT[p] || p}</span>)}
      </button>
    );
  }
  if (row.hunt_state === 'none') return <span className="dash-muted" title="This flaw leaves no reliable trace in common logs">Not huntable</span>;
  return <span className="dash-muted" title="Queries are written when the brief is first opened, or in the background">Open to generate</span>;
}

/**
 * The CVE table. A row opens the brief in the side panel; the Hunt cell opens it on the hunting tab.
 * With the panel docked the secondary columns hide so the list stays readable beside it.
 */
export default function VulnTable({ rows, selectedId, onOpen, docked, scope }) {
  return (
    <div className="dash-table-wrap vp-wrap">
      <table className={`dash-table vp-table ${docked ? 'is-docked' : ''}`}>
        <thead>
          <tr>
            <th scope="col">CVE</th>
            <th scope="col">Priority</th>
            <th scope="col">CVSS</th>
            <th scope="col" title="FIRST EPSS: probability of exploitation in the next 30 days">EPSS</th>
            <th scope="col">Exploitation</th>
            <th scope="col" className="vp-col-2nd">Vendor / product</th>
            <th scope="col" className="vp-col-2nd">{scope === 'kev' ? 'Added to KEV' : 'Published'}</th>
            <th scope="col" title="Threat hunting queries">Hunt queries</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const selected = selectedId === r.cve_id;
            const product = [r.vendor, r.product].filter(Boolean).join(' ');
            const date = scope === 'kev' ? r.kev_date_added : (r.published || r.kev_date_added);
            return (
              <tr key={r.cve_id} className={selected ? 'is-open' : undefined} aria-selected={selected} onClick={() => onOpen(r.cve_id)} data-testid={`cve-card-${r.cve_id}`}>
                <td className="vp-id">
                  <button type="button" className="vp-link dash-mono" onClick={(e) => { e.stopPropagation(); onOpen(r.cve_id); }} aria-label={`Open ${r.cve_id}`}>{r.cve_id}</button>
                  <span className="vp-sub dash-ellipsis" title={r.title || r.summary || r.related_threats?.[0]?.name || undefined}>
                    {r.title || r.summary || r.related_threats?.[0]?.name || ''}
                  </span>
                  {r.trend_note && <span className="vp-trend"><TrendingUp aria-hidden="true" /> {r.trend_note}</span>}
                  {r.related_count > 0 && <span className="vp-cited" title={r.related_threats?.[0]?.name}>Cited in {r.related_count} {r.related_count === 1 ? 'threat' : 'threats'}</span>}
                </td>
                <td data-label="Priority"><PriorityBadge decision={r.priority} title={(r.priority_reasons || []).join('. ')} /></td>
                <td data-label="CVSS"><Cvss row={r} /></td>
                <td data-label="EPSS" className="dash-mono dash-epss">{r.epss != null ? pct(r.epss) : <span className="dash-muted">-</span>}</td>
                <td data-label="Exploitation"><Exploitation row={r} /></td>
                <td data-label="Product" className="vp-col-2nd"><span className="dash-ellipsis">{product || <span className="dash-muted">-</span>}</span>{r.in_stack && <Badge size="sm" tone="info" title={`Matches ${r.in_stack}, which you declared`}>In your stack</Badge>}</td>
                <td data-label="Date" className="dash-mono vp-col-2nd">{isoDate(date) || <span className="dash-muted">-</span>}</td>
                <td data-label="Hunt queries"><Hunt row={r} onOpen={onOpen} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
